<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <div v-if="isLoading" class="text-center text-gray-600 mt-4">
      Chargement en cours...
    </div>

    <div v-else class="mb-8">
      <!-- Toolbar modernisée avec icônes -->
      <div class="anamnesis-detail-toolbar">
        <div class="anamnesis-detail-toolbar-content">
          <!-- Section titre avec icône et gestion des permissions -->
          <div class="anamnesis-detail-title-section">
            <!-- Icône selon le type de personne -->
            <div class="flex items-center mr-3">
              <BuildingIcon v-if="agent?.is_legal_entity" class="h-6 w-6 text-blue-600" />
              <UserIcon v-else class="h-6 w-6 text-green-600" />
            </div>

            <div class="relative flex-1">
              <!-- Édition pour personne morale : un seul champ affiliation -->
              <input v-if="isEditingTitle && canEdit && agent?.is_legal_entity" ref="affiliationInput"
                v-model="editedAffiliation" type="text" placeholder="Nom de la personne morale"
                class="anamnesis-detail-title-edit" @blur="handleTitleUpdate" @keydown.enter="handleTitleUpdate"
                @keydown.escape="cancelTitleEdit" />

              <!-- Édition pour personne physique : prénom et nom -->
              <template v-else-if="isEditingTitle && canEdit && !agent?.is_legal_entity">
                <input ref="firstNameInput" v-model="editedFirstName" type="text" placeholder="Prénom"
                  class="anamnesis-detail-title-edit mr-2" @blur="handleTitleUpdate" @keydown.enter="handleTitleUpdate"
                  @keydown.escape="cancelTitleEdit" />
                <input v-model="editedLastName" type="text" placeholder="Nom" class="anamnesis-detail-title-edit"
                  @blur="handleTitleUpdate" @keydown.enter="handleTitleUpdate" @keydown.escape="cancelTitleEdit" />
              </template>

              <!-- Affichage normal -->
              <h1 v-else class="anamnesis-detail-title" :class="{ readonly: !canEdit }"
                @click="canEdit && startTitleEdit()">
                {{ getDisplayName(agent) }}
              </h1>
            </div>

            <!-- Lien AureHAL -->
            <div v-if="agent?.idref_id && !isEditing" class="ml-4">
              <a :href="getAureHalLink(agent.idref_id)" target="_blank" class="anamnesis-detail-external-link">
                <LinkIcon class="h-4 w-4" />
                <span>Voir sur AureHAL</span>
              </a>
            </div>

            <!-- ID HAL en mode édition -->
            <div v-if="isEditing && agent?.idref_id" class="ml-4 text-sm text-gray-500">
              ID HAL: {{ agent.idref_id }}
            </div>
          </div>

          <!-- Actions avec gestion des permissions - masquage au lieu de désactivation -->
          <div class="anamnesis-detail-actions">
            <!-- Toggle public/privé - visible pour tous, modifiable seulement par le propriétaire -->
            <button class="anamnesis-detail-status" :class="[
              agent?.is_public ? 'public' : 'private',
              { disabled: !canEdit }
            ]" :disabled="!canEdit" @click="canEdit && handlePublicToggle()">
              <EyeIcon v-if="agent?.is_public" class="h-4 w-4" />
              <EyeOffIcon v-else class="h-4 w-4" />
              {{ agent?.is_public ? 'Public' : 'Privé' }}
            </button>

            <!-- Boutons d'action - seulement visibles pour les propriétaires/admin -->
            <template v-if="canEdit">
              <!-- Boutons d'édition -->
              <template v-if="isEditing">
                <button class="anamnesis-detail-btn-success" @click="handleSave">
                  <CheckIcon class="h-5 w-5" />
                  Valider
                </button>
                <button class="anamnesis-detail-btn-secondary" @click="handleCancel">
                  <XIcon class="h-5 w-5" />
                  Annuler
                </button>
              </template>
              <button v-else class="anamnesis-detail-btn-primary" @click="handleEdit">
                <PencilIcon class="h-5 w-5" />
              </button>

              <!-- Supprimer - seulement pour les propriétaires/admin -->
              <button v-if="canDelete" class="anamnesis-detail-btn-delete" @click="handleDelete">
                <TrashIcon class="h-5 w-5" />
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Grille principale avec fond blanc et cadres -->
      <div class="anamnesis-detail-grid">
        <!-- Colonne de gauche : Détails -->
        <div class="anamnesis-detail-section bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
            Informations {{ agent?.is_legal_entity ? 'de la personne morale' : 'personnelles' }}
          </h3>

          <!-- Affiliation - seulement pour les personnes physiques -->
          <div v-if="!agent?.is_legal_entity" class="anamnesis-detail-field">
            <Building2Icon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">Affiliation</label>
              <div v-if="isEditing">
                <input v-model="editedAgent.affiliation" class="anamnesis-detail-field-input"
                  placeholder="Affiliation" />
                <div v-if="editedAgent.affiliation_id" class="mt-1 text-sm text-gray-500">
                  ID Structure: {{ editedAgent.affiliation_id }}
                </div>
              </div>
              <div v-else>
                <p class="anamnesis-detail-field-text">
                  {{ agent?.affiliation || 'Aucune affiliation' }}
                </p>
                <a v-if="agent?.affiliation_id" :href="getStructureLink(agent.affiliation_id)" target="_blank"
                  class="anamnesis-detail-external-link mt-1">
                  <LinkIcon class="h-4 w-4" />
                  <span>Voir sur AureHAL</span>
                </a>
              </div>
            </div>
          </div>

          <!-- Groupe -->
          <div class="anamnesis-detail-field">
            <UsersIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ agent?.is_legal_entity ? 'Département/Service' : 'Groupe de recherche' }}
              </label>
              <div v-if="isEditing">
                <input v-model="editedAgent.group" class="anamnesis-detail-field-input"
                  :placeholder="agent?.is_legal_entity ? 'Département ou service' : 'Groupe de recherche'" />
              </div>
              <p v-else class="anamnesis-detail-field-text">
                {{ agent?.group || (agent?.is_legal_entity ? 'Aucun département' : 'Aucun groupe') }}
              </p>
            </div>
          </div>

          <!-- Rôles -->
          <div class="anamnesis-detail-field">
            <UserCogIcon class="anamnesis-detail-field-icon" />
            <div class="anamnesis-detail-field-content">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ agent?.is_legal_entity ? 'Types d\'activité' : 'Rôles' }}
              </label>
              <div v-if="isEditing">
                <TagsPicker v-model="editedAgent.roles" />
              </div>
              <div v-else class="flex flex-wrap gap-2">
                <span v-for="role in getRoles(agent?.roles)" :key="role"
                  class="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                  {{ role }}
                </span>
                <span v-if="!agent?.roles" class="anamnesis-detail-field-empty">
                  {{ agent?.is_legal_entity ? 'Aucune activité définie' : 'Aucun rôle défini' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Colonne de droite : Métadonnées avec fond blanc -->
        <div class="anamnesis-detail-metadata bg-white rounded-lg shadow-sm p-6">
          <h3 class="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Métadonnées</h3>

          <div class="anamnesis-detail-meta-item">
            <ClockIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Créé le</p>
              <p class="anamnesis-detail-meta-text">
                {{ formatDate(agent?.created_at) }}
              </p>
            </div>
          </div>

          <div v-if="agent?.updated_at && agent.updated_at !== agent.created_at" class="anamnesis-detail-meta-item">
            <ClockIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Modifié le</p>
              <p class="anamnesis-detail-meta-text">
                {{ formatDate(agent?.updated_at) }}
              </p>
            </div>
          </div>

          <div v-if="detailedMemos.length" class="anamnesis-detail-meta-item">
            <BookOpenIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Mémos associés</p>
              <p class="anamnesis-detail-meta-text">
                {{ detailedMemos.length }} mémo(s)
              </p>
            </div>
          </div>

          <div class="anamnesis-detail-meta-item">
            <UserIcon class="anamnesis-detail-meta-icon" />
            <div>
              <p class="text-sm font-medium text-gray-700">Propriétaire</p>
              <p class="anamnesis-detail-meta-text">
                {{ ownerName || 'Chargement...' }}
              </p>
            </div>
          </div>

          <!-- Type de personne -->
          <div class="anamnesis-detail-meta-item">
            <div class="flex items-center mr-2">
              <BuildingIcon v-if="agent?.is_legal_entity" class="h-5 w-5 text-blue-600" />
              <UserIcon v-else class="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p class="text-sm font-medium text-gray-700">Type</p>
              <p class="anamnesis-detail-meta-text">
                {{ agent?.is_legal_entity ? 'Personne morale' : 'Personne physique' }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Mémos avec fond blanc -->
      <div v-if="canViewSubsets" class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <h2 class="anamnesis-detail-memos-title">Mémos associés</h2>
        <div v-if="detailedMemos.length">
          <MemosSubset :memos="detailedMemos" :show-delete-button="false" @view="handleMemoView" />
        </div>
        <div v-else class="anamnesis-detail-memos-empty">
          Aucun mémo associé à cet agent
        </div>
      </div>
      
      <!-- Message d'accès restreint -->
      <div v-else class="anamnesis-detail-memos-section bg-white rounded-lg shadow-sm p-6">
        <div class="text-center py-8">
          <EyeOffIcon class="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">Accès restreint</h3>
          <p class="text-gray-600">
            {{ !authStore.isAuthenticated ? 'Connectez-vous pour voir les mémos associés' : 'Cet asset privé ne vous appartient pas' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Composants utilitaires -->
    <ScrollToTop />
    <Notification ref="notificationRef" />
    <ModalAreYouSure v-model="showDeleteModal" title="Supprimer l'agent"
      message="Êtes-vous sûr de vouloir supprimer cet agent ? Cette action est irréversible." @confirm="confirmDelete"
      @cancel="showDeleteModal = false" />
  </div>
</template>

<script setup lang="ts">
import {
    BookOpen as BookOpenIcon,
    Building2 as Building2Icon,
    Building as BuildingIcon,
    Check as CheckIcon,
    Clock as ClockIcon,
    Eye as EyeIcon,
    EyeOff as EyeOffIcon,
    Link as LinkIcon,
    Pencil as PencilIcon,
    Trash as TrashIcon,
    UserCog as UserCogIcon,
    User as UserIcon,
    Users as UsersIcon,
    X as XIcon
} from 'lucide-vue-next';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// Composants
import Breadcrumbs from '../Breadcrumbs.vue';
import Notification from '../Notification.vue';
import TagsPicker from '../common/TagsPicker.vue';
import MemosSubset from '../subsets/MemosSubset.vue';
import ScrollToTop from '../utils/ScrollToTop.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

// Types
import type { AgentWithMemos } from '@/api/types/entities/agent/relations';
import type { Memo } from '@/api/types/memos/types';

// API et stores
import { agents } from '@/api/agents';
import { fetchMemosUuid } from '@/api/memos';
import { useAssetsCacheStore } from '@/stores/assetsCache';
import { useAuthStore } from '@/stores/auth';
import { useBreadcrumbsStore } from '@/stores/breadcrumbs';
import { useUserNamesCacheStore } from '@/stores/userNamesCache';

// Props
interface Props {
  uuid: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<Props>();

// Composables
const route = useRoute();
const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();
const assetsCacheStore = useAssetsCacheStore();
const userNamesCacheStore = useUserNamesCacheStore();

// État principal
const agent = ref<AgentWithMemos | null>(null);
const detailedMemos = ref<Memo[]>([]);
const isLoading = ref(true);
const isEditing = ref(false);
const isEditingTitle = ref(false);
const showDeleteModal = ref(false);
const ownerName = ref<string>('');

// Références
const notificationRef = ref<InstanceType<typeof Notification> | null>(null);
const firstNameInput = ref<HTMLInputElement | null>(null);
const affiliationInput = ref<HTMLInputElement | null>(null);

// État d'édition
const editedAgent = ref<Partial<AgentWithMemos>>({});
const editedFirstName = ref('');
const editedLastName = ref('');
const editedAffiliation = ref('');

// Gestion des permissions
const isMyAsset = computed((): boolean => {
  return agent.value?.user_id === authStore.user?.uuid;
});

const canEdit = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canDelete = computed((): boolean => {
  return authStore.isAdmin || isMyAsset.value;
});

const canViewSubsets = computed((): boolean => {
  // Peut voir les subsets si :
  // 1. L'asset est public, OU
  // 2. L'utilisateur est connecté ET est propriétaire de l'asset
  return agent.value?.is_public || (authStore.isAuthenticated && isMyAsset.value);
});

// Récupérer l'état depuis history.state
const historyState = window.history.state;
agent.value = historyState?.agent || null;

onMounted(async (): Promise<void> => {
  await loadAgent();
});

const loadDetailedMemos = async (): Promise<void> => {
  if (!agent.value?.memos) return;

  try {
    const memoPromises = agent.value.memos.map(memo =>
      fetchMemosUuid(memo.uuid)
    );
    const memosData = await Promise.all(memoPromises);
    detailedMemos.value = memosData.map(data => data[0]);
  } catch (error) {
    console.error('Erreur lors de la récupération des détails des mémos:', error);
  }
};

const loadAgent = async (): Promise<void> => {
  if (agent.value) {
    updateBreadcrumb();
    await loadDetailedMemos();
    await loadOwnerName();
    isLoading.value = false;
    return;
  }

  try {
    // Si l'utilisateur est connecté, utiliser l'API normale
    if (authStore.isAuthenticated) {
      const agentData = await agents.fetchAgentWithMemos(route.params.uuid as string);
      agent.value = agentData;
    } else {
      // Sinon, utiliser l'API publique qui permet l'accès par UUID
      const agentData = await agents.fetchPublicAgentWithMemos(route.params.uuid as string);
      agent.value = agentData;
    }
    updateBreadcrumb();
    await loadDetailedMemos();
    await loadOwnerName();
  } catch (error) {
    console.error('Erreur lors du chargement de l\'agent:', error);
    if (authStore.isAuthenticated) {
      await router.push('/myagents');
    } else {
      await router.push('/');
    }
  } finally {
    isLoading.value = false;
  }
};

const loadOwnerName = async (): Promise<void> => {
  if (!agent.value?.user_id) {
    ownerName.value = 'Inconnu';
    return;
  }

  try {
    const name = await userNamesCacheStore.getUserNameAsync(agent.value.user_id);
    ownerName.value = name || 'Utilisateur privé';
  } catch (error) {
    console.error('Erreur lors du chargement du nom du propriétaire:', error);
    ownerName.value = 'Erreur de chargement';
  }
};

const updateBreadcrumb = (): void => {
  if (!agent.value) {
    const breadcrumbPath = authStore.isAuthenticated ? '/myagents' : '/';
    const breadcrumbName = authStore.isAuthenticated ? 'Mes agents' : 'Accueil';
    breadcrumbsStore.updateBreadcrumbs([
      { name: breadcrumbName, path: breadcrumbPath },
      { name: 'Agent', path: route.fullPath }
    ]);
    return;
  }

  // Gérer les personnes morales et physiques
  const agentName = agent.value.is_legal_entity
    ? (agent.value.affiliation || 'Personne morale')
    : `${agent.value.first_name || ''} ${agent.value.last_name || ''}`.trim() || 'Agent';

  const breadcrumbPath = authStore.isAuthenticated ? '/myagents' : '/';
  const breadcrumbName = authStore.isAuthenticated ? 'Mes agents' : 'Accueil';

  breadcrumbsStore.updateBreadcrumbs([
    { name: breadcrumbName, path: breadcrumbPath },
    { name: agentName, path: route.fullPath }
  ]);
};

const resetEditedData = (): void => {
  if (agent.value) {
    editedAgent.value = {
      idref_id: agent.value.idref_id,
      affiliation: agent.value.affiliation,
      affiliation_id: agent.value.affiliation_id,
      group: agent.value.group,
      roles: agent.value.roles,
      is_public: agent.value.is_public
    };
    editedFirstName.value = agent.value.first_name || '';
    editedLastName.value = agent.value.last_name || '';
  }
};

const handleEdit = (): void => {
  if (!canEdit.value) return;
  resetEditedData();
  isEditing.value = true;
};

const handleCancel = (): void => {
  isEditing.value = false;
  isEditingTitle.value = false;
  resetEditedData();
};

const startTitleEdit = (): void => {
  if (!canEdit.value || !agent.value) return;

  if (agent.value.is_legal_entity) {
    editedAffiliation.value = agent.value.affiliation || '';
  } else {
    editedFirstName.value = agent.value.first_name || '';
    editedLastName.value = agent.value.last_name || '';
  }

  isEditingTitle.value = true;

  nextTick((): void => {
    if (agent.value?.is_legal_entity) {
      affiliationInput.value?.focus();
    } else {
      firstNameInput.value?.focus();
    }
  });
};

const cancelTitleEdit = (): void => {
  isEditingTitle.value = false;
  if (agent.value) {
    if (agent.value.is_legal_entity) {
      editedAffiliation.value = agent.value.affiliation || '';
    } else {
      editedFirstName.value = agent.value.first_name || '';
      editedLastName.value = agent.value.last_name || '';
    }
  }
};

const handleTitleUpdate = async (): Promise<void> => {
  if (!agent.value || !canEdit.value) return;

  let updateData: Partial<AgentWithMemos>;
  let hasChanges = false;

  if (agent.value.is_legal_entity) {
    // Pour les personnes morales, on met à jour l'affiliation
    updateData = {
      affiliation: editedAffiliation.value.trim()
    };
    hasChanges = updateData.affiliation !== agent.value.affiliation;
  } else {
    // Pour les personnes physiques, on met à jour prénom/nom
    updateData = {
      first_name: editedFirstName.value.trim(),
      last_name: editedLastName.value.trim()
    };
    hasChanges = updateData.first_name !== agent.value.first_name ||
      updateData.last_name !== agent.value.last_name;
  }

  if (!hasChanges) {
    isEditingTitle.value = false;
    return;
  }

  try {
    const updatedAgent = await agents.updateAgent(agent.value.uuid, updateData);
    agent.value = { ...updatedAgent, memos: agent.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('agents', agent.value);

    isEditingTitle.value = false;
    updateBreadcrumb();

    const successMessage = agent.value.is_legal_entity
      ? 'Nom de la personne morale mis à jour avec succès'
      : 'Nom mis à jour avec succès';

    notificationRef.value?.showNotification(successMessage, 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour:', error);
    notificationRef.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handleSave = async (): Promise<void> => {
  if (!agent.value || !canEdit.value) return;

  const updateData = {
    first_name: editedFirstName.value || '',
    last_name: editedLastName.value || '',
    idref_id: editedAgent.value.idref_id || agent.value.idref_id,
    affiliation: editedAgent.value.affiliation || agent.value.affiliation,
    affiliation_id: editedAgent.value.affiliation_id || agent.value.affiliation_id,
    group: editedAgent.value.group || agent.value.group,
    roles: editedAgent.value.roles || agent.value.roles,
    is_public: editedAgent.value.is_public !== undefined ? editedAgent.value.is_public : agent.value.is_public,
    user_id: agent.value.user_id
  };

  try {
    const updatedAgent = await agents.updateAgent(agent.value.uuid, updateData);
    agent.value = { ...updatedAgent, memos: agent.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('agents', agent.value);

    isEditing.value = false;
    isEditingTitle.value = false;
    updateBreadcrumb();
    notificationRef.value?.showNotification('Agent mis à jour avec succès', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'agent:', error);
    notificationRef.value?.showNotification('Erreur lors de la mise à jour', 'error');
  }
};

const handleDelete = (): void => {
  if (!canDelete.value) return;
  showDeleteModal.value = true;
};

const handlePublicToggle = async (): Promise<void> => {
  if (!agent.value?.uuid || !canEdit.value) return;

  try {
    const updatedAgent = await agents.updateAgent(agent.value.uuid, {
      is_public: !agent.value.is_public
    });
    agent.value = { ...updatedAgent, memos: agent.value.memos };

    // Mettre à jour le cache
    assetsCacheStore.updateAssetInCache('agents', agent.value);

    notificationRef.value?.showNotification('Visibilité mise à jour', 'success');
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la visibilité:', error);
    notificationRef.value?.showNotification('Erreur lors de la mise à jour de la visibilité', 'error');
  }
};

const confirmDelete = async (): Promise<void> => {
  if (!agent.value?.uuid || !canDelete.value) return;

  try {
    await agents.deleteAgent(agent.value.uuid);

    // Supprimer du cache
    assetsCacheStore.removeAssetFromCache('agents', agent.value.uuid);

    notificationRef.value?.showNotification('Agent supprimé avec succès', 'success');
    router.push('/myagents');
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'agent:', error);
    notificationRef.value?.showNotification('Erreur lors de la suppression de l\'agent', 'error');
  }
  showDeleteModal.value = false;
};

// Fonctions utilitaires
const getAureHalLink = (idref_id: string): string | undefined => {
  const [firstId, secondId] = idref_id.split('-');

  if (secondId && secondId !== '0') {
    return `https://aurehal.archives-ouvertes.fr/person/read/id/${secondId}`;
  }

  if (firstId && firstId !== '0') {
    return `https://aurehal.archives-ouvertes.fr/person/browse?critere=form_i:"${firstId}"`;
  }

  return undefined;
};

const getStructureLink = (affiliation_id: string): string => {
  return `https://aurehal.archives-ouvertes.fr/structure/read/id/${affiliation_id}`;
};

const getRoles = (roleslist?: string): string[] => {
  if (!roleslist) return [];
  return roleslist.split(',').map(role => role.trim());
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

const handleMemoView = (memo: Memo): void => {
  router.push(`/memos/${memo.uuid}`);
};

// Fonction utilitaire pour afficher le nom selon le type d'agent
const getDisplayName = (agent: AgentWithMemos | null): string => {
  if (!agent) return 'Sans nom';
  if (agent.is_legal_entity) {
    return agent.affiliation || 'Personne morale';
  }
  return [agent.first_name, agent.last_name].filter(Boolean).join(' ') || 'Sans nom';
};
</script>